<template>
    <v-container fluid>
        <v-row>

            <v-col cols="12">
                <div class="d-flex">
                    <v-alert v-show="isSystem" type="info" icon="mdi-alert" class="mt-2 mb-0 body-2">
                        This variable is required by the system and therefore cannot be renamed or deleted.
                    </v-alert>
                </div>

                <div class="d-flex">
                    <v-alert v-show="!isSystem && !isEditable" type="warning" icon="mdi-alert" class="mt-2 mb-0 body-2">
                        This variable belongs to the Standard Profiling Questions -library and therefore the question
                        text
                        and/or options should not be modified beyond their original intent.
                    </v-alert>
                </div>
            </v-col>

            <v-col cols="12" md="8">

                <title-card :title="ucFirst(variableType)" :side-icon="icon" help="edit-datavariable-settings">

                    <!-- VARIABLE LABEL -->
                    <v-text-field
                        :disabled="!isEditable"
                        v-model="variableName"
                        placeholder="Please enter label"
                        autocomplete="false"
                        ref="variableName"
                        :autofocus="this.dataVariable && this.dataVariable.name.length === 0"
                        :counter="100"
                        required
                        @blur="emit()"
                    ></v-text-field>

                    <!-- OPTION EDITOR -->
                    <option-editor
                        v-if="hasOptions"
                        class="mt-5"
                        :data-variable="variable"
                        :available-locales="availableLocales"
                        @option:updated="optionUpdated"
                        @change="optionsChanged"
                        :enable-add="variable.user_editable"
                        :enable-delete="variable.user_editable"
                        name="data variable"
                    ></option-editor>

                </title-card>
            </v-col>

            <v-col cols="12" md="4">

                <title-card title="Settings" side-icon="mdi-cog" help="edit-datavariable-options">

                    <v-switch
                        color="primary"
                        class="mt-0"
                        label="Personally Identifiable (PI)"
                        v-model="variablePi"
                        hide-details
                    ></v-switch>

                    <v-switch v-show="isApiWritable"
                              color="primary"
                              label="Community API writable"
                              v-model="communityApiWritableModel"
                              hide-details
                    ></v-switch>

                </title-card>
            </v-col>
        </v-row>

        <v-row v-if="variable && variable.type === 'date'">
            <v-col cols="12">
                <ranges-tab v-if="variable"
                            :data-variable="variable"
                            @change="rangesUpdated"
                ></ranges-tab>
            </v-col>
        </v-row>

        <json v-if="false" :data="variable"></json>

    </v-container>

</template>

<script>

import OptionEditor from '../OptionEditor';
import Json from 'vue-json-pretty';
import TitleCardList from "../../../../components/UI/TitleCardList";
import TitleCard from "../../../../components/UI/TitleCard";
import RangesTab from "../AgeRangeConfigurator";

export default {

    components: {
        RangesTab,
        Json,
        OptionEditor,
        TitleCard,
        TitleCardList
    },

    props: {
        dataVariable: {
            type: Object | null,
            required: true,
        },
        availableLocales: {
            type: Array | null,
            required: true
        }
    },

    watch: {
        // Async loading watch
        dataVariable(variable) {
            // Deep clone
            this.variable = JSON.parse(JSON.stringify(variable));
        },
    },

    mounted() {
        // If user is coming from the list page (both props are populated)
        if (this.dataVariable) {
            this.variable = JSON.parse(JSON.stringify(this.dataVariable));
        }
    },

    data() {
        return {
            debug: false,
            variable: null,
            variableLabelUnderEdit: null,
        }
    },

    computed: {
        icon() {
            if (this.variable) {
                return this.$store.state.dataVariableIcons[this.variable.type];
            }
        },

        variableName: {
            get() {
                if (this.variable) {
                    return this.variable.name;
                }
            },
            set(name) {
                this.variable.name = name.toUpperCase();
            }
        },

        variablePi: {
            get() {
                if (this.variable) {
                    return this.variable.pi;
                }
            },
            set(pi) {
                this.variable.pi = pi;
                this.emit();
            }
        },

        communityApiWritableModel: {
            get() {
                if (this.variable) {
                    return this.variable.community_api_writable;
                }
            },
            set(value) {
                this.variable.community_api_writable = value;
                this.emit();
            }
        },

        isSystem() {
            if (this.variable) return this.variable.system;
        },

        isEditable() {
            if (this.variable) return this.variable.user_editable;
        },

        isLocalizable() {
            if (this.variable) return this.variable.user_localizable;
        },

        isWritable() {
            if (this.variable) return this.variable.panelist_writable;
        },

        variableType() {
            if (this.variable) return this.variable.type;
        },

        hasOptions() {
            return this.variable && (this.variable.type === 'radio' || this.variable.type === 'checkbox');
        },

        isApiWritable() {
            if (this.variable) {
                if (this.variable.system && !this.variable.panelist_writable) return false;
                return this.variable.panelist_writable;
            }
            return false;
        }
    },

    methods: {

        /**
         * Ranges updated
         *
         * @param variable
         */
        rangesUpdated(variable) {
            this.variable = Object.assign({}, variable);
            this.emit();
        },

        /**
         * Single option updated
         */
        optionUpdated({updatedOption, oldOption}) {

            if (!oldOption) throw new error('Could not find old option in OptionEditor.vue');

            if (oldOption.name !== updatedOption.name) {
                const index = this.variable.options.findIndex(option => option.uuid === updatedOption.uuid);
                this.variable.options.splice(index, 1, updatedOption);
            }

            this.emit();
        },

        /**
         * Option editor has updated options
         *
         * param Array updateOptions
         */
        optionsChanged(updateOptions) {
            this.variable.options = [...updateOptions];
            this.emit();
        },

        emit() {
            this.$emit('change', Object.assign({}, this.variable));
        }
    }
}
</script>
